import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { CheckCircle, Music, Printer, ChevronLeft, SkipForward, UserCheck, Delete } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const APPARATUS_LABELS = {
  vault: 'Vault', wagvault: 'Vault', magvault: 'Vault',
  bars: 'Bars', wagbars: 'Bars',
  beam: 'Beam', wagbeam: 'Beam',
  floor: 'Floor', wagfloor: 'Floor', magfloor: 'Floor',
  range: 'Range',
};

// ── On-screen keyboard — QWERTY or numeric-only ──────────────────────────────
function OnScreenKeyboard({ value, onChange, placeholder = 'Start typing...', numOnly = false }) {
  const [caps, setCaps] = useState(true);

  const rows = [
    ['Q','W','E','R','T','Y','U','I','O','P'],
    ['A','S','D','F','G','H','J','K','L'],
    ['Z','X','C','V','B','N','M'],
  ];
  const nums = ['1','2','3','4','5','6','7','8','9','0'];

  const tap    = k  => onChange((value || '') + (caps ? k.toUpperCase() : k.toLowerCase()));
  const tapRaw = ch => onChange((value || '') + ch);
  const back   = () => onChange((value || '').slice(0, -1));
  const clear  = () => onChange('');

  const keyBtn = 'flex items-center justify-center bg-white border border-gray-200 rounded-xl font-bold shadow-sm active:scale-95 hover:bg-gray-50 transition-transform select-none';

  // ── Input display (shared) ──
  const InputDisplay = () => (
    <div className="bg-gray-50 border-2 border-gray-200 rounded-2xl px-6 py-3 text-4xl font-bold text-center min-h-[4.5rem] tracking-widest overflow-hidden shrink-0">
      {value ? <span className="break-all">{value}</span> : <span className="text-gray-300 text-2xl">{placeholder}</span>}
    </div>
  );

  // ── Numeric-only keypad ──
  if (numOnly) return (
    <div className="flex flex-col gap-3 w-full max-w-sm mx-auto select-none">
      <InputDisplay />
      {[['1','2','3'],['4','5','6'],['7','8','9']].map((row, i) => (
        <div key={i} className="flex gap-3">
          {row.map(k => (
            <button key={k} onClick={() => tapRaw(k)} className="flex-1 h-14 bg-white border border-gray-200 rounded-2xl text-3xl font-bold shadow-sm active:scale-95 hover:bg-gray-50">
              {k}
            </button>
          ))}
        </div>
      ))}
      <div className="flex gap-3">
        <button onClick={clear} className="flex-1 h-14 bg-gray-100 border border-gray-200 rounded-2xl text-sm font-bold text-gray-500 active:scale-95">CLEAR</button>
        <button onClick={() => tapRaw('0')} className="flex-1 h-14 bg-white border border-gray-200 rounded-2xl text-3xl font-bold shadow-sm active:scale-95 hover:bg-gray-50">0</button>
        <button onClick={back} className="flex-1 h-14 bg-red-50 border border-red-200 rounded-2xl text-red-500 active:scale-95 flex items-center justify-center">
          <Delete className="w-6 h-6" />
        </button>
      </div>
    </div>
  );

  // ── Full QWERTY keyboard ──
  return (
    <div className="flex flex-col gap-1.5 w-full select-none">
      <InputDisplay />

      {/* Number row + backspace */}
      <div className="flex gap-1 justify-center">
        {nums.map(k => (
          <button key={k} onClick={() => tapRaw(k)} className={`${keyBtn} w-11 h-11 text-base`}>{k}</button>
        ))}
        <button onClick={back} className={`${keyBtn} w-13 h-11 px-2 text-sm bg-red-50 border-red-200 text-red-500 ml-1`}>
          <Delete className="w-4 h-4" />
        </button>
      </div>

      {/* Letter rows */}
      {rows.map((row, ri) => (
        <div key={ri} className="flex gap-1 justify-center">
          {ri === 2 && (
            <button onClick={() => setCaps(c => !c)} className={`${keyBtn} w-14 h-11 text-xs mr-0.5 ${caps ? 'bg-black text-white border-black' : ''}`}>
              {caps ? '↑ ABC' : '↑ abc'}
            </button>
          )}
          {row.map(k => (
            <button key={k} onClick={() => tap(k)} className={`${keyBtn} w-11 h-11 text-base`}>
              {caps ? k : k.toLowerCase()}
            </button>
          ))}
          {ri === 2 && (
            <button onClick={clear} className={`${keyBtn} w-14 h-11 text-xs ml-0.5 bg-gray-100 text-gray-500`}>CLEAR</button>
          )}
        </div>
      ))}

      {/* Bottom: hyphen, apostrophe, space, period */}
      <div className="flex gap-1.5 justify-center">
        <button onClick={() => tapRaw('-')}  className={`${keyBtn} w-11 h-11 text-lg`}>-</button>
        <button onClick={() => tapRaw("'")}  className={`${keyBtn} w-11 h-11 text-lg`}>'</button>
        <button onClick={() => tapRaw(' ')}  className={`${keyBtn} flex-1 h-11 text-sm max-w-xs`}>SPACE</button>
        <button onClick={() => tapRaw('.')}  className={`${keyBtn} w-11 h-11 text-lg`}>.</button>
      </div>
    </div>
  );
}

// ── Header shown on every step ───────────────────────────────────────────────
function Header({ step, totalSteps, rightSlot }) {
  return (
    <div className="flex items-center justify-between px-10 py-6 border-b border-gray-100 shrink-0">
      <img src="/scorz-logo.png" alt="Scorz" className="h-12 w-auto" draggable={false} />
      <div className="flex items-center gap-6">
        <div className="flex gap-2">
          {Array.from({ length: totalSteps }).map((_, i) => (
            <div
              key={i}
              className={`h-2 rounded-full transition-all ${
                i < step ? 'w-10 bg-black' : i === step ? 'w-10 bg-black' : 'w-6 bg-gray-200'
              }`}
            />
          ))}
        </div>
        {rightSlot}
      </div>
    </div>
  );
}

// Walk-in step meta: order and labels
const WALKIN_STEPS = ['firstname', 'lastname', 'bg', 'qual', 'review'];

// ── Main wizard ───────────────────────────────────────────────────────────────
export default function SelfRegWizard({ competitionId, round, competition }) {
  // ── Gymnast mode state ───────────────────────────────────────────────────────
  const [step, setStep] = useState('club');
  const [clubs, setClubs] = useState([]);
  const [allGymnasts, setAllGymnasts] = useState([]);
  const [loadingGymnasts, setLoadingGymnasts] = useState(false);
  const [selectedClub, setSelectedClub] = useState(null);
  const [selectedGymnast, setSelectedGymnast] = useState(null);
  const [clubFilter, setClubFilter] = useState('');
  const [competingApparatus, setCompetingApparatus] = useState([]);
  const [musicConfirmed, setMusicConfirmed] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const audioRef = useRef(null);
  const doneTimerRef = useRef(null);

  // ── Coach mode state ─────────────────────────────────────────────────────────
  const [coachMode, setCoachMode] = useState(false);
  const [coachStep, setCoachStep] = useState('club');
  const [allCoaches, setAllCoaches] = useState([]);
  const [coachClubs, setCoachClubs] = useState([]);
  const [loadingCoaches, setLoadingCoaches] = useState(false);
  const [selectedCoachClub, setSelectedCoachClub] = useState(null);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [coachClubFilter, setCoachClubFilter] = useState('');
  const [submittingCoach, setSubmittingCoach] = useState(false);
  const coachDoneTimerRef = useRef(null);

  // ── Coach walk-in state ──────────────────────────────────────────────────────
  const [coachWalkin, setCoachWalkin] = useState(false);
  const [walkinStep, setWalkinStep] = useState('firstname');
  const [walkinFirstName, setWalkinFirstName] = useState('');
  const [walkinLastName, setWalkinLastName] = useState('');
  const [walkinBg, setWalkinBg] = useState('');
  const [walkinQual, setWalkinQual] = useState('');
  const [submittingWalkin, setSubmittingWalkin] = useState(false);

  const roundNumber = round?.roundNumber;
  const availableApparatus = competition?.competitionApparatus?.length
    ? competition.competitionApparatus
    : ['vault', 'bars', 'beam', 'floor'];

  const showPrint     = competition?.checkInPrintNumberEnabled !== false;
  const showApparatus = competition?.checkInCompetingApparatusEnabled !== false;
  const showMusic     = competition?.checkInMusicEnabled !== false;

  const STEPS = ['club', 'gymnast', 'confirm',
    ...(showPrint     ? ['print']     : []),
    ...(showApparatus ? ['apparatus'] : []),
    ...(showMusic     ? ['music']     : []),
    'done'];
  const stepIndex = STEPS.indexOf(step);
  const totalSteps = STEPS.length;

  // ── Load gymnasts ───────────────────────────────────────────────────────────
  const loadGymnasts = useCallback(() => {
    if (!competitionId || !roundNumber) return;
    setLoadingGymnasts(true);
    axios.get(`${API}/registration/gymnasts-by-round/${competitionId}/${roundNumber}`)
      .then(res => {
        const sorted = [...res.data].sort((a, b) => {
          const na = `${a.preferredFirstName || a.firstName} ${a.preferredLastName || a.lastName}`.toLowerCase();
          const nb = `${b.preferredFirstName || b.firstName} ${b.preferredLastName || b.lastName}`.toLowerCase();
          return na.localeCompare(nb);
        });
        setAllGymnasts(sorted);
        const uniqueClubs = [];
        const seen = new Set();
        sorted.forEach(g => {
          if (!seen.has(g.clubId)) { seen.add(g.clubId); uniqueClubs.push({ id: g.clubId, name: g.clubName }); }
        });
        setClubs(uniqueClubs.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {})
      .finally(() => setLoadingGymnasts(false));
  }, [competitionId, roundNumber]);

  // ── Load coaches ────────────────────────────────────────────────────────────
  const loadCoaches = useCallback(() => {
    if (!competitionId) return;
    setLoadingCoaches(true);
    axios.get(`${API}/registration/coaches/${competitionId}`)
      .then(res => {
        const coaches = res.data.coaches || [];
        setAllCoaches(coaches);
        const seen = new Set();
        const clubList = [];
        coaches.forEach(c => {
          if (!seen.has(c.clubId) && c.clubId) {
            seen.add(c.clubId);
            clubList.push({ id: c.clubId, name: c.clubName });
          }
        });
        setCoachClubs(clubList.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => {})
      .finally(() => setLoadingCoaches(false));
  }, [competitionId]);

  // Initial gymnast load
  useEffect(() => { loadGymnasts(); }, [loadGymnasts]);

  // Poll every 10 s on idle gymnast steps for terminal sync
  useEffect(() => {
    if (step !== 'club' && step !== 'gymnast') return;
    const id = setInterval(loadGymnasts, 10000);
    return () => clearInterval(id);
  }, [step, loadGymnasts]);

  // Auto-play music on music step
  useEffect(() => {
    if (step !== 'music' || !selectedGymnast) return;
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.src = `${BACKEND_URL}/api/audio/floor-music/${selectedGymnast.id}`;
      audioRef.current.load();
      audioRef.current.play()
        .then(() => setIsPlaying(true))
        .catch(() => setIsPlaying(false));
    }
  }, [step, selectedGymnast]);

  // Auto-reset gymnast done screen
  useEffect(() => {
    if (step !== 'done') return;
    doneTimerRef.current = setTimeout(resetWizard, 8000);
    return () => clearTimeout(doneTimerRef.current);
  }, [step]);

  // Auto-reset coach done screen
  useEffect(() => {
    if (!coachMode || coachStep !== 'done') return;
    coachDoneTimerRef.current = setTimeout(resetCoach, 6000);
    return () => clearTimeout(coachDoneTimerRef.current);
  }, [coachMode, coachStep]);

  // Default apparatus on gymnast selection
  useEffect(() => {
    if (selectedGymnast) setCompetingApparatus([...availableApparatus]);
  }, [selectedGymnast]); // eslint-disable-line

  // ── Reset helpers ───────────────────────────────────────────────────────────
  const resetWizard = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current.src = ''; }
    clearTimeout(doneTimerRef.current);
    setStep('club');
    setSelectedClub(null);
    setSelectedGymnast(null);
    setClubFilter('');
    setCompetingApparatus([]);
    setMusicConfirmed(null);
    setIsPlaying(false);
  };

  const resetCoach = () => {
    clearTimeout(coachDoneTimerRef.current);
    setCoachMode(false);
    setCoachStep('club');
    setSelectedCoachClub(null);
    setSelectedCoach(null);
    setCoachClubFilter('');
    resetWalkin();
    loadCoaches();
  };

  const resetWalkin = () => {
    setCoachWalkin(false);
    setWalkinStep('firstname');
    setWalkinFirstName('');
    setWalkinLastName('');
    setWalkinBg('');
    setWalkinQual('');
  };

  const enterCoachMode = () => {
    loadCoaches();
    setCoachStep('club');
    setSelectedCoachClub(null);
    setSelectedCoach(null);
    setCoachClubFilter('');
    resetWalkin();
    setCoachMode(true);
  };

  const advance = () => setStep(STEPS[STEPS.indexOf(step) + 1] || 'done');

  // ── Submit helpers ──────────────────────────────────────────────────────────
  const handleSubmitCheckIn = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/registration/check-in`, {
        competitionId,
        roundNumber: round?.roundNumber,
        roundName: round?.roundName || '',
        groupName: selectedGymnast.groupName || '',
        gymnastId: selectedGymnast.id,
        musicCorrect: musicConfirmed,
        spectatorCount: 0,
        phoneNumber: null,
        wantsLiveScores: false,
        competingApparatus,
      });
    } catch (e) {
      console.error('Check-in failed', e);
    }
    setSubmitting(false);
    loadGymnasts();
    setStep('done');
  };

  const handleCoachCheckIn = async () => {
    if (submittingCoach || !selectedCoach) return;
    setSubmittingCoach(true);
    try {
      await axios.post(`${API}/registration/coach-check-in`, {
        competitionId,
        coachId: selectedCoach.id,
      });
    } catch (e) {
      console.error('Coach check-in failed', e);
    }
    setSubmittingCoach(false);
    setCoachStep('done');
  };

  const handleWalkinSubmit = async () => {
    if (submittingWalkin) return;
    setSubmittingWalkin(true);
    try {
      await axios.post(`${API}/registration/coach-walkin`, {
        competitionId,
        clubId: selectedCoachClub?.id,
        firstName: walkinFirstName.trim(),
        lastName: walkinLastName.trim(),
        bgNumber: walkinBg.trim(),
        qualification: walkinQual.trim(),
      });
      // Populate selectedCoach for the done-screen greeting
      setSelectedCoach({ firstName: walkinFirstName.trim(), lastName: walkinLastName.trim(), id: null });
      resetWalkin();
      setCoachStep('done');
    } catch (e) {
      console.error('Walk-in failed', e);
    }
    setSubmittingWalkin(false);
  };

  // ── Display helpers ─────────────────────────────────────────────────────────
  const firstName    = g => g?.preferredFirstName || g?.firstName || '';
  const lastName     = g => g?.preferredLastName  || g?.lastName  || '';
  const fullName     = g => `${firstName(g)} ${lastName(g)}`.trim();
  const coachFullName = c => `${c?.firstName || ''} ${c?.lastName || ''}`.trim();

  const clubGymnasts = selectedClub
    ? allGymnasts.filter(g => g.clubId === selectedClub.id && !g.checkInStatus?.checkedIn && !g.checkInStatus?.withdrawn)
    : [];
  const filteredClubs = clubs.filter(c => c.name.toLowerCase().includes(clubFilter.toLowerCase()));

  const clubCoaches = selectedCoachClub
    ? allCoaches.filter(c => c.clubId === selectedCoachClub.id && !c.checkedIn)
    : [];
  const filteredCoachClubs = coachClubs.filter(c => c.name.toLowerCase().includes(coachClubFilter.toLowerCase()));

  // Subtle coach button shown on gymnast club step only
  const coachModeButton = (
    <button
      data-testid="coach-mode-btn"
      onClick={enterCoachMode}
      className="flex items-center gap-1.5 text-sm text-gray-300 hover:text-gray-500 transition-colors px-2 py-1 rounded select-none"
    >
      <UserCheck className="w-4 h-4" />
      Coach registration
    </button>
  );

  // ── Shared coach header ─────────────────────────────────────────────────────
  const CoachHeader = ({ onBack, backLabel }) => (
    <div className="flex items-center justify-between px-10 py-6 border-b border-gray-100 shrink-0">
      <img src="/scorz-logo.png" alt="Scorz" className="h-12 w-auto" draggable={false} />
      <div className="flex items-center gap-4">
        <span className="text-sm font-semibold text-gray-500 uppercase tracking-widest">Coach Registration</span>
        {onBack && (
          <button onClick={onBack} className="text-sm text-gray-300 hover:text-gray-500 transition-colors px-2 py-1" data-testid="coach-back-btn">
            {backLabel || 'Back'}
          </button>
        )}
      </div>
    </div>
  );

  // ════════════════════════════════════════════════════════════════════════════
  // COACH WALK-IN FORM
  // ════════════════════════════════════════════════════════════════════════════

  if (coachMode && coachWalkin) {
    const walkinStepIndex = WALKIN_STEPS.indexOf(walkinStep);

    const WalkinLayout = ({ title, subtitle, value, onChange, onContinue, continueLabel, onBack, canContinue, placeholder, children }) => (
      <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
        <div className="flex items-center justify-between px-8 py-4 border-b border-gray-100 shrink-0">
          <img src="/scorz-logo.png" alt="Scorz" className="h-10 w-auto" draggable={false} />
          <div className="flex items-center gap-4">
            <div className="flex gap-1.5">
              {WALKIN_STEPS.map((_, i) => (
                <div key={i} className={`h-2 rounded-full transition-all ${i <= walkinStepIndex ? 'w-8 bg-black' : 'w-5 bg-gray-200'}`} />
              ))}
            </div>
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Coach Registration</span>
          </div>
        </div>
        <div className="flex-1 flex flex-col px-8 py-4 gap-3 overflow-hidden">
          <div className="shrink-0">
            <h1 className="text-3xl font-black text-gray-900">{title}</h1>
            {subtitle && <p className="text-base text-gray-400 mt-0.5">{subtitle}</p>}
          </div>
          <div className="flex-1 flex flex-col gap-3 overflow-hidden min-h-0">
            {children || (
              <OnScreenKeyboard value={value} onChange={onChange} placeholder={placeholder} />
            )}
          </div>
          {onContinue && (
            <div className="flex gap-3 shrink-0 pt-1">
              {onBack && (
                <button onClick={onBack} className="px-6 py-3 rounded-2xl border-2 border-gray-200 text-lg font-bold text-gray-500 hover:border-gray-400 active:scale-95 transition-all">
                  Back
                </button>
              )}
              <button
                onClick={onContinue}
                disabled={!canContinue}
                className="flex-1 bg-black hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-2xl py-3 text-xl font-bold transition-all active:scale-95"
                data-testid="walkin-continue-btn"
              >
                {continueLabel || 'Continue →'}
              </button>
            </div>
          )}
        </div>
      </div>
    );

    // Step: First name
    if (walkinStep === 'firstname') return (
      <WalkinLayout
        title="What is your first name?"
        value={walkinFirstName}
        onChange={setWalkinFirstName}
        placeholder="First name"
        onContinue={() => setWalkinStep('lastname')}
        canContinue={walkinFirstName.trim().length > 0}
        onBack={() => { resetWalkin(); }}
      />
    );

    // Step: Last name
    if (walkinStep === 'lastname') return (
      <WalkinLayout
        title="What is your last name?"
        subtitle={`First name: ${walkinFirstName}`}
        value={walkinLastName}
        onChange={setWalkinLastName}
        placeholder="Last name"
        onContinue={() => setWalkinStep('bg')}
        canContinue={walkinLastName.trim().length > 0}
        onBack={() => setWalkinStep('firstname')}
      />
    );

    // Step: BG number — numeric keypad only
    if (walkinStep === 'bg') return (
      <WalkinLayout
        title="What is your BG number?"
        subtitle="Your British Gymnastics licence number (you can skip this)"
        onContinue={() => setWalkinStep('qual')}
        canContinue={true}
        continueLabel={walkinBg.trim() ? 'Continue →' : 'Skip →'}
        onBack={() => setWalkinStep('lastname')}
      >
        <OnScreenKeyboard value={walkinBg} onChange={setWalkinBg} placeholder="e.g. 12345678" numOnly />
      </WalkinLayout>
    );

    // Step: Qualification — presets + keyboard, WalkinLayout provides the buttons
    if (walkinStep === 'qual') {
      const presets = ['WAG L1', 'WAG L2', 'WAG L3', 'WAG L4', 'WAG L5'];
      return (
        <WalkinLayout
          title="What is your qualification?"
          subtitle="Tap a preset or type your own"
          onContinue={() => setWalkinStep('review')}
          canContinue={true}
          continueLabel={walkinQual.trim() ? 'Continue →' : 'Skip →'}
          onBack={() => setWalkinStep('bg')}
        >
          <div className="flex flex-wrap gap-2 shrink-0">
            {presets.map(p => (
              <button
                key={p}
                onClick={() => setWalkinQual(p)}
                className={`px-3 py-1.5 rounded-xl text-sm font-bold border-2 transition-all active:scale-95 ${
                  walkinQual === p ? 'bg-black text-white border-black' : 'bg-white text-gray-700 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`walkin-qual-preset-${p.replace(/\s+/g,'-').toLowerCase()}`}
              >
                {p}
              </button>
            ))}
          </div>
          <OnScreenKeyboard value={walkinQual} onChange={setWalkinQual} placeholder="Or type your qualification" />
        </WalkinLayout>
      );
    }

    // Step: Review
    if (walkinStep === 'review') {
      const rows = [
        { label: 'First name',    value: walkinFirstName },
        { label: 'Last name',     value: walkinLastName },
        { label: 'Club',          value: selectedCoachClub?.name || '—' },
        { label: 'BG number',     value: walkinBg || '—' },
        { label: 'Qualification', value: walkinQual || '—' },
      ];
      return (
        <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
          <div className="flex items-center justify-between px-8 py-4 border-b border-gray-100 shrink-0">
            <img src="/scorz-logo.png" alt="Scorz" className="h-10 w-auto" draggable={false} />
            <div className="flex items-center gap-4">
              <div className="flex gap-1.5">
                {WALKIN_STEPS.map((_, i) => (
                  <div key={i} className={`h-2 rounded-full transition-all ${i <= walkinStepIndex ? 'w-8 bg-black' : 'w-5 bg-gray-200'}`} />
                ))}
              </div>
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest">Coach Registration</span>
            </div>
          </div>
          <div className="flex-1 flex flex-col px-8 py-6 gap-5 overflow-hidden">
            <h1 className="text-3xl font-black text-gray-900 shrink-0">Confirm your details</h1>
            <div className="bg-gray-50 rounded-2xl border-2 border-gray-200 overflow-hidden shrink-0">
              {rows.map((r, i) => (
                <div key={r.label} className={`flex items-center px-6 py-4 ${i < rows.length - 1 ? 'border-b border-gray-200' : ''}`}>
                  <span className="w-32 text-sm font-semibold text-gray-400 uppercase tracking-wide shrink-0">{r.label}</span>
                  <span className="text-xl font-bold text-gray-900">{r.value}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-4 mt-auto">
              <button onClick={() => setWalkinStep('qual')} className="px-6 py-3 rounded-2xl border-2 border-gray-200 text-lg font-bold text-gray-500 hover:border-gray-400 active:scale-95 transition-all">
                Back
              </button>
              <button
                data-testid="walkin-submit-btn"
                onClick={handleWalkinSubmit}
                disabled={submittingWalkin}
                className="flex-1 bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white rounded-2xl py-3 text-xl font-bold transition-all active:scale-95"
              >
                {submittingWalkin ? 'Registering...' : 'Confirm & Register →'}
              </button>
            </div>
          </div>
        </div>
      );
    }
  }

  // ════════════════════════════════════════════════════════════════════════════
  // COACH MODE STEPS
  // ════════════════════════════════════════════════════════════════════════════

  if (coachMode) {
    // ── COACH: Club step ──────────────────────────────────────────────────────
    if (coachStep === 'club') return (
      <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
        <CoachHeader onBack={() => resetCoach()} backLabel="Back to gymnast registration" />
        <div className="flex-1 flex flex-col px-10 py-8 gap-6 overflow-hidden">
          <h1 className="text-5xl font-black text-gray-900">Select your club</h1>

          {coachClubs.length > 6 && (
            <input
              type="text"
              value={coachClubFilter}
              onChange={e => setCoachClubFilter(e.target.value)}
              placeholder="Search clubs..."
              className="w-full border-2 border-gray-200 rounded-2xl px-6 py-4 text-2xl focus:outline-none focus:border-black"
            />
          )}

          {loadingCoaches ? (
            <div className="flex-1 flex items-center justify-center">
              <p className="text-2xl text-gray-400">Loading...</p>
            </div>
          ) : coachClubs.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-6">
              <p className="text-3xl text-gray-400 text-center">No coaches found for this competition.</p>
              <p className="text-xl text-gray-300 text-center">Coaches must be added to entries before they can check in here.</p>
              <button onClick={resetCoach} className="mt-2 bg-black text-white rounded-2xl px-10 py-5 text-2xl font-bold">Back</button>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto grid grid-cols-3 gap-4 content-start pb-4">
              {filteredCoachClubs.map(club => {
                const coachCount = allCoaches.filter(c => c.clubId === club.id && !c.checkedIn).length;
                return (
                  <button
                    key={club.id}
                    data-testid={`coach-club-tile-${club.id}`}
                    onClick={() => { setSelectedCoachClub(club); setCoachStep('select'); }}
                    className="bg-gray-50 hover:bg-black hover:text-white active:scale-95 border-2 border-gray-200 hover:border-black rounded-2xl p-6 text-left transition-all"
                  >
                    <div className="text-2xl font-bold leading-tight">{club.name}</div>
                    <div className="text-base text-gray-400 mt-1">
                      {coachCount} coach{coachCount !== 1 ? 'es' : ''}
                    </div>
                  </button>
                );
              })}
              {filteredCoachClubs.length === 0 && (
                <div className="col-span-3 flex items-center justify-center py-20">
                  <p className="text-2xl text-gray-400">No clubs found</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );

    // ── COACH: Select coach ───────────────────────────────────────────────────
    if (coachStep === 'select') return (
      <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
        <CoachHeader />
        <div className="flex-1 flex flex-col px-10 py-8 gap-6 overflow-hidden">
          <div className="flex items-center gap-4 shrink-0">
            <button
              onClick={() => { setSelectedCoachClub(null); setCoachStep('club'); }}
              className="flex items-center gap-2 text-2xl text-gray-400 hover:text-black transition-colors"
              data-testid="coach-back-to-clubs-btn"
            >
              <ChevronLeft className="w-8 h-8" /> {selectedCoachClub?.name}
            </button>
          </div>
          <h1 className="text-5xl font-black text-gray-900 shrink-0">Who are you?</h1>

          {clubCoaches.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-6">
              <p className="text-3xl text-gray-400 text-center">All coaches from this club are already checked in.</p>
              <button
                data-testid="coach-walkin-btn"
                onClick={() => { setCoachWalkin(true); setWalkinStep('firstname'); }}
                className="bg-black text-white rounded-2xl px-10 py-5 text-2xl font-bold active:scale-95 transition-all"
              >
                I don't show up — register here
              </button>
              <button onClick={() => { setSelectedCoachClub(null); setCoachStep('club'); }} className="text-xl text-gray-400 hover:text-black transition-colors">
                Back to clubs
              </button>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden gap-4">
              <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-4 content-start pb-2">
                {clubCoaches.map(coach => (
                  <button
                    key={coach.id}
                    data-testid={`coach-tile-${coach.id}`}
                    onClick={() => { setSelectedCoach(coach); setCoachStep('confirm'); }}
                    className="bg-gray-50 hover:bg-black hover:text-white active:scale-95 border-2 border-gray-200 hover:border-black rounded-2xl p-6 text-left transition-all"
                  >
                    <div className="text-2xl font-bold">{coachFullName(coach)}</div>
                    <div className="text-base text-gray-400 mt-1">{coach.clubName}</div>
                  </button>
                ))}
              </div>
              {/* "I don't show up" fallback — always visible at the bottom */}
              <div className="shrink-0 pt-2 border-t border-gray-100">
                <button
                  data-testid="coach-walkin-btn"
                  onClick={() => { setCoachWalkin(true); setWalkinStep('firstname'); }}
                  className="w-full py-4 rounded-2xl border-2 border-dashed border-gray-300 text-gray-400 hover:border-gray-500 hover:text-gray-600 text-xl font-semibold transition-all active:scale-95"
                >
                  I don't show up — register as a new coach
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );

    // ── COACH: Confirm ────────────────────────────────────────────────────────
    if (coachStep === 'confirm') return (
      <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
        <CoachHeader />
        <div className="flex-1 flex flex-col items-center justify-center gap-10 px-10 py-8">
          <p className="text-3xl text-gray-500 font-medium tracking-wide uppercase">Is this you?</p>
          <div className="text-center">
            <div className="text-6xl font-black text-gray-900 leading-tight">{coachFullName(selectedCoach)}</div>
            <div className="text-3xl text-gray-400 mt-3 font-medium">{selectedCoachClub?.name}</div>
          </div>
          <div className="flex gap-6 w-full max-w-3xl mt-4">
            <button
              data-testid="coach-confirm-no-btn"
              onClick={() => { setSelectedCoach(null); setCoachStep('select'); }}
              className="flex-1 bg-white border-2 border-gray-200 hover:border-red-400 hover:bg-red-50 text-gray-700 hover:text-red-600 rounded-3xl py-8 text-3xl font-bold transition-all active:scale-95"
            >
              No, that's not me
            </button>
            <button
              data-testid="coach-confirm-yes-btn"
              onClick={handleCoachCheckIn}
              disabled={submittingCoach}
              className="flex-1 bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white rounded-3xl py-8 text-3xl font-bold transition-all active:scale-95"
            >
              {submittingCoach ? 'Registering...' : "Yes, that's me!"}
            </button>
          </div>
        </div>
      </div>
    );

    // ── COACH: Done ───────────────────────────────────────────────────────────
    if (coachStep === 'done') return (
      <div className="h-screen bg-white flex flex-col items-center justify-center gap-8 overflow-hidden select-none px-10">
        <CheckCircle className="w-40 h-40 text-green-500" strokeWidth={1.5} />
        <div className="text-center">
          <h1 className="text-6xl font-black text-gray-900">Welcome!</h1>
          <p className="text-3xl text-gray-500 mt-3">
            Good luck today, {selectedCoach?.firstName || 'coach'}!
          </p>
        </div>
        <button
          data-testid="coach-done-btn"
          onClick={resetCoach}
          className="mt-6 bg-black hover:bg-gray-800 text-white rounded-3xl px-16 py-6 text-3xl font-bold transition-all active:scale-95"
        >
          Done
        </button>
        <p className="text-gray-300 text-lg mt-2">Returns automatically in a few seconds...</p>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // GYMNAST MODE STEPS
  // ════════════════════════════════════════════════════════════════════════════

  // ── STEP: Club ───────────────────────────────────────────────────────────────
  if (step === 'club') return (
    <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
      <Header step={stepIndex} totalSteps={totalSteps} rightSlot={coachModeButton} />
      <div className="flex-1 flex flex-col px-10 py-8 gap-6 overflow-hidden">
        <h1 className="text-5xl font-black text-gray-900">Select your club</h1>

        {clubs.length > 6 && (
          <input
            type="text"
            value={clubFilter}
            onChange={e => setClubFilter(e.target.value)}
            placeholder="Search clubs..."
            className="w-full border-2 border-gray-200 rounded-2xl px-6 py-4 text-2xl focus:outline-none focus:border-black"
          />
        )}

        {loadingGymnasts ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-2xl text-gray-400">Loading...</p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto grid grid-cols-3 gap-4 content-start pb-4">
            {filteredClubs.map(club => (
              <button
                key={club.id}
                data-testid={`club-tile-${club.id}`}
                onClick={() => { setSelectedClub(club); setStep('gymnast'); }}
                className="bg-gray-50 hover:bg-black hover:text-white active:scale-95 border-2 border-gray-200 hover:border-black rounded-2xl p-6 text-left transition-all"
              >
                <div className="text-2xl font-bold leading-tight">{club.name}</div>
                <div className="text-base text-gray-400 mt-1">
                  {allGymnasts.filter(g => g.clubId === club.id && !g.checkInStatus?.checkedIn && !g.checkInStatus?.withdrawn).length} gymnast(s)
                </div>
              </button>
            ))}
            {filteredClubs.length === 0 && (
              <div className="col-span-3 flex items-center justify-center py-20">
                <p className="text-2xl text-gray-400">No clubs found</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ── STEP: Gymnast ────────────────────────────────────────────────────────────
  if (step === 'gymnast') return (
    <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
      <Header step={stepIndex} totalSteps={totalSteps} />
      <div className="flex-1 flex flex-col px-10 py-8 gap-6 overflow-hidden">
        <div className="flex items-center gap-4 shrink-0">
          <button
            onClick={() => { setSelectedClub(null); setStep('club'); }}
            className="flex items-center gap-2 text-2xl text-gray-400 hover:text-black transition-colors"
          >
            <ChevronLeft className="w-8 h-8" /> {selectedClub?.name}
          </button>
        </div>
        <h1 className="text-5xl font-black text-gray-900 shrink-0">Who are you?</h1>

        {clubGymnasts.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <p className="text-3xl text-gray-400 text-center">No gymnasts to check in from this club.</p>
            <button
              onClick={() => { setSelectedClub(null); setStep('club'); }}
              className="mt-4 bg-black text-white rounded-2xl px-10 py-5 text-2xl font-bold"
            >
              Back to clubs
            </button>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto grid grid-cols-2 gap-4 content-start pb-4">
            {clubGymnasts.map(g => (
              <button
                key={g.id}
                data-testid={`gymnast-tile-${g.id}`}
                onClick={() => { setSelectedGymnast(g); setStep('confirm'); }}
                className="bg-gray-50 hover:bg-black hover:text-white active:scale-95 border-2 border-gray-200 hover:border-black rounded-2xl p-6 text-left transition-all"
              >
                {g.programmeNumber && (
                  <div className="text-5xl font-black leading-none mb-2">{g.programmeNumber}</div>
                )}
                <div className="text-2xl font-bold">{fullName(g)}</div>
                <div className="text-base text-gray-400 mt-1">{g.categoryName || ''}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  // ── STEP: Confirm ────────────────────────────────────────────────────────────
  if (step === 'confirm') return (
    <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
      <Header step={stepIndex} totalSteps={totalSteps} />
      <div className="flex-1 flex flex-col items-center justify-center gap-10 px-10 py-8">
        <p className="text-3xl text-gray-500 font-medium tracking-wide uppercase">Is this you?</p>
        <div className="text-center">
          {selectedGymnast?.programmeNumber && (
            <div className="text-[140px] font-black leading-none text-gray-900 mb-2">
              {selectedGymnast.programmeNumber}
            </div>
          )}
          <div className="text-6xl font-black text-gray-900 leading-tight">{fullName(selectedGymnast)}</div>
          <div className="text-3xl text-gray-400 mt-3 font-medium">{selectedClub?.name}</div>
        </div>
        <div className="flex gap-6 w-full max-w-3xl mt-4">
          <button
            data-testid="confirm-no-btn"
            onClick={resetWizard}
            className="flex-1 bg-white border-2 border-gray-200 hover:border-red-400 hover:bg-red-50 text-gray-700 hover:text-red-600 rounded-3xl py-8 text-3xl font-bold transition-all active:scale-95"
          >
            No, that's not me
          </button>
          <button
            data-testid="confirm-yes-btn"
            onClick={advance}
            className="flex-1 bg-black hover:bg-gray-800 text-white rounded-3xl py-8 text-3xl font-bold transition-all active:scale-95"
          >
            Yes, that's me!
          </button>
        </div>
      </div>
    </div>
  );

  // ── STEP: Print ───────────────────────────────────────────────────────────────
  if (step === 'print') return (
    <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
      <Header step={stepIndex} totalSteps={totalSteps} />
      <div className="flex-1 flex flex-col items-center justify-center gap-8 px-10 py-8">
        <h1 className="text-4xl font-black text-gray-900">Print your number</h1>
        <div className="bg-gray-50 border-2 border-gray-200 rounded-3xl px-20 py-10 text-center">
          {selectedGymnast?.programmeNumber
            ? <div className="text-[120px] font-black leading-none text-gray-900">{selectedGymnast.programmeNumber}</div>
            : <div className="text-3xl text-gray-400 py-8">No number assigned yet</div>
          }
          <div className="text-3xl font-bold text-gray-700 mt-2">{fullName(selectedGymnast)}</div>
        </div>
        <div className="flex gap-5 w-full max-w-2xl">
          <button
            data-testid="print-btn"
            onClick={async () => {
              if (!selectedGymnast?.programmeNumber) return;
              setIsPrinting(true);
              const pdfUrl = `${API}/registration/gymnast-number-pdf/${competitionId}/${selectedGymnast.id}`;
              const w = window.open(pdfUrl, '_blank');
              if (w) w.onload = () => setTimeout(() => w.print(), 500);
              setIsPrinting(false);
              advance();
            }}
            disabled={isPrinting || !selectedGymnast?.programmeNumber}
            className="flex-1 flex flex-col items-center gap-3 bg-black hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-3xl py-8 text-2xl font-bold transition-all active:scale-95"
          >
            <Printer className="w-10 h-10" />
            {isPrinting ? 'Opening...' : 'Print Number'}
          </button>
          <button
            data-testid="skip-print-btn"
            onClick={advance}
            className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-3xl px-8 py-8 text-xl font-bold transition-all active:scale-95"
          >
            <SkipForward className="w-6 h-6" /> Skip
          </button>
        </div>
      </div>
    </div>
  );

  // ── STEP: Apparatus ───────────────────────────────────────────────────────────
  if (step === 'apparatus') return (
    <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
      <Header step={stepIndex} totalSteps={totalSteps} />
      <div className="flex-1 flex flex-col items-center justify-center gap-8 px-10 py-8">
        <h1 className="text-4xl font-black text-gray-900 text-center">What are you competing today?</h1>
        <p className="text-2xl text-gray-400 text-center">Tap to deselect anything you're NOT competing</p>
        <div className={`grid gap-4 w-full max-w-3xl ${availableApparatus.length <= 4 ? 'grid-cols-4' : 'grid-cols-3'}`}>
          {availableApparatus.map(app => {
            const selected = competingApparatus.includes(app);
            return (
              <button
                key={app}
                data-testid={`apparatus-toggle-${app}`}
                onClick={() => setCompetingApparatus(prev =>
                  prev.includes(app) ? prev.filter(a => a !== app) : [...prev, app]
                )}
                className={`rounded-3xl py-8 text-2xl font-black transition-all active:scale-95 border-2 ${
                  selected ? 'bg-black text-white border-black' : 'bg-white text-gray-300 border-gray-200'
                }`}
              >
                {APPARATUS_LABELS[app] || app}
              </button>
            );
          })}
        </div>
        <button
          data-testid="apparatus-continue-btn"
          onClick={advance}
          disabled={competingApparatus.length === 0}
          className="bg-black hover:bg-gray-800 disabled:bg-gray-200 disabled:text-gray-400 text-white rounded-3xl px-20 py-6 text-3xl font-bold transition-all active:scale-95 mt-4"
        >
          Continue
        </button>
      </div>
    </div>
  );

  // ── STEP: Music ───────────────────────────────────────────────────────────────
  if (step === 'music') return (
    <div className="h-screen bg-white flex flex-col overflow-hidden select-none">
      <Header step={stepIndex} totalSteps={totalSteps} />
      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} />
      <div className="flex-1 flex flex-col items-center justify-center gap-8 px-10 py-8">
        <h1 className="text-4xl font-black text-gray-900">Your floor music</h1>
        <p className="text-2xl text-gray-400 text-center">Listen and confirm this is your music</p>
        <button
          onClick={() => {
            if (!audioRef.current) return;
            if (isPlaying) { audioRef.current.pause(); setIsPlaying(false); }
            else { audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {}); }
          }}
          className="w-48 h-48 rounded-full bg-black hover:bg-gray-800 text-white flex items-center justify-center transition-all active:scale-95 shadow-2xl"
        >
          {isPlaying ? (
            <svg className="w-20 h-20" fill="currentColor" viewBox="0 0 24 24">
              <rect x="6" y="4" width="4" height="16" rx="1" /><rect x="14" y="4" width="4" height="16" rx="1" />
            </svg>
          ) : (
            <Music className="w-20 h-20 ml-4" />
          )}
        </button>
        <div className="flex gap-5 w-full max-w-2xl mt-4">
          <button
            data-testid="music-wrong-btn"
            onClick={() => {
              setMusicConfirmed(false);
              if (audioRef.current) { audioRef.current.pause(); setIsPlaying(false); }
              handleSubmitCheckIn();
            }}
            className="flex-1 bg-white border-2 border-gray-200 hover:border-orange-400 hover:bg-orange-50 text-gray-600 hover:text-orange-600 rounded-3xl py-7 text-2xl font-bold transition-all active:scale-95"
          >
            That's not my music
          </button>
          <button
            data-testid="music-correct-btn"
            onClick={() => {
              setMusicConfirmed(true);
              if (audioRef.current) { audioRef.current.pause(); setIsPlaying(false); }
              handleSubmitCheckIn();
            }}
            className="flex-1 bg-black hover:bg-gray-800 text-white rounded-3xl py-7 text-2xl font-bold transition-all active:scale-95"
          >
            Yes, that's my music!
          </button>
        </div>
      </div>
    </div>
  );

  // ── STEP: Done ────────────────────────────────────────────────────────────────
  if (step === 'done') return (
    <div className="h-screen bg-white flex flex-col items-center justify-center gap-8 overflow-hidden select-none px-10">
      <CheckCircle className="w-40 h-40 text-green-500" strokeWidth={1.5} />
      <div className="text-center">
        <h1 className="text-6xl font-black text-gray-900">All done!</h1>
        <p className="text-3xl text-gray-500 mt-3">Have a great competition, {firstName(selectedGymnast)}!</p>
      </div>
      <button
        data-testid="done-start-again-btn"
        onClick={resetWizard}
        className="mt-6 bg-black hover:bg-gray-800 text-white rounded-3xl px-16 py-6 text-3xl font-bold transition-all active:scale-95"
      >
        Done
      </button>
      <p className="text-gray-300 text-lg mt-2">Returns automatically in a few seconds...</p>
    </div>
  );

  if (submitting) return (
    <div className="h-screen bg-white flex flex-col items-center justify-center gap-6 overflow-hidden select-none">
      <div className="w-16 h-16 border-4 border-gray-200 border-t-black rounded-full animate-spin" />
      <p className="text-3xl font-bold text-gray-700">Registering...</p>
    </div>
  );

  return null;
}
